type ReadonlyRecord<K extends string, T> = Readonly<Record<K, T>>;

export enum AppLanguageCode {
    nl = "nl",
    fr = "fr",
    en = "en",
    de = "de",
    it = "it",
    es = "es",
}

export enum AppCountryCode {
    be = "BE",
    nl = "NL",
    fr = "FR",
    de = "DE",
    it = "IT",
    sp = "SP",
}

export type Pagination = {
    readonly per_page: number;
    readonly total: number;
    readonly current_page: number;
};

type ReadonlyNonEmptyArray<A> = ReadonlyArray<A> & {
    readonly 0: A;
};

// Base internal objects -----------------------------------------------------------------------------------------------
export enum SortDirection { // TODO: should be removed!
    asc = "asc",
    desc = "desc",
}

export enum SavedListType /* TODO: check still accurate? */ {
    favorites = "favorites",
}

export enum VehicleSortAttribute { // TODO: should be removed
    creationDate = "creationDate",
    discount = "discount",
    price = "price",
    vehicleLabel = "vehicleLabel",
}

export type PaginationQueryParameters = {
    /* Pagination */
    per_page: null | number;
    current_page: null | number;
    sort_field: number;
    sort_direction: number;
};

export type LanguageDto = {
    id: number;
    name: string;
    iso2_code: AppLanguageCode;
};

// Base Dtos -----------------------------------------------------------------------------------------------------------

export type SortConfigurationDto = {
    id_direction: number;
    id_field: number;
};

export type SortOptionDto = {
    readonly description: string;
    readonly configuration: SortConfigurationDto;
};

export type GetEnumSortDataDto = {
    readonly default_option: SortOptionDto;
    readonly available_options: SortOptionDto[];
};

export type StatusDto = {
    readonly code: number;
    readonly message: string;
    readonly innerMessage: string;
};

export type VehicleDocumentDto = {
    readonly id: number;
    readonly documentId: number;
    readonly url: string;
    readonly name: string;
    readonly vehicleId: number;
    readonly documentType: string;
};

export type APIResponse<T> = {
    readonly data: T;
    readonly status: StatusDto;
    readonly pagination_metadata: PaginationMetadata;
    readonly documents?: VehicleDocumentDto[];
};

// Registration & Authentication & Users -------------------------------------------------------------------------------
export type PostAuthRegisterRequest = {
    readonly id_company: number | null;
    readonly firstname: string;
    readonly lastname: string;
    readonly email: string;
    readonly password: string;
    readonly password_confirmation: string;
    phone: string;
    mobile: string;
    readonly id_language: number | null;
    readonly id_gender: number | null;
    readonly date_of_birth: string | null;
    readonly company_name: string;
    readonly vat_number: string;
    readonly marketing_subscription: boolean;
};

export interface SessionDto {
    readonly token: string;
    readonly refresh_token: string;
}

export type UserRoleDto = {
    readonly id: number;
    readonly name: string;
};

export enum EnumUserRole {
    // DEACTIVATED = 1,
    VIEWER = 1,
    PURCHASOR = 2,
    ADMINISTRATOR = 3,
}

export type GenderDto = {
    readonly id: number;
    readonly name: string;
};

export interface UserApprovalStatusDto {
    id: number;
    name: string;
}

export interface UserApprovalTypeDto {
    id: number;
    name: string;
}

export type ContactDto = {
    readonly id: number;
    readonly id_company: number;
    readonly firstname: string;
    readonly lastname: string;
    readonly email: string;
    readonly language: LanguageDto;
    readonly in_service: boolean;
    readonly gender: GenderDto | null;
    readonly date_of_birth: number;
    readonly marketing_subscription: boolean;
    readonly marketing_on_company_profile: boolean;
};

export type UserDto = ContactDto & {
    readonly role: UserRoleDto;
    readonly is_enabled: boolean;
    readonly is_email_verified: boolean;
    readonly approval: ApprovalDTO;
};

export type ApprovalDTO = {
    status: OrderStatusDto;
    type: OrderTypeDto;
};

export type PatchUserRequestBody = {
    firstname: string;
    lastname: string;
    id_language: number;
    id_gender: number | undefined;
    date_of_birth: Date | undefined;
    marketing_subscription: boolean;
    marketing_on_company_profile: boolean;
};

export type PatchFinancialRecordRequestBody = {
    invoicing_email: string;
};

export type PutAuthPasswordRequestBody = {
    old_password: string;
    new_password: string;
};

// Company Data --------------------------------------------------------------------------------------------------------
export enum EnumCompanyStatus {
    DISCONTINUED = 1,
    DISCONTINUED_ONESIDED = 2,
    DISCONTINUED_BANKRUPTCY = 3,
    INACTIVE = 4,
    MISSING_MANDATORY_INFO = 5,
    AWAITING_APPROVAL = 6,
    APPROVED = 7,
}

export type CompanyStatusDto = {
    readonly id: number;
    readonly name: string;
};

export type CompanyType = {
    readonly id: number;
    readonly name: string;
};

export type BrandType = {
    readonly id: number;
    readonly name: string;
};

export type BodyType = {
    readonly id: number;
    readonly name: string;
};

export type CountryDto = {
    readonly id: number;
    readonly name: string;
    readonly iso2: string;
    readonly iso3: string;
    readonly default_language: LanguageDto;
};

export type PhoneNumberTypeDto = {
    readonly id: number;
    readonly name: string | null;
};

export type PhoneNumberDto = {
    readonly id: number | null;
    readonly number: string;
    phoneNumberIsValid: boolean;
    edit?: boolean;
    readonly type: PhoneNumberTypeDto | null;
};

export type PostUserPhoneNumbersRequestBody = {
    readonly id_type: number;
    readonly number: string | null;
};

export type CompanyAddressDto = {
    readonly street: string;
    readonly number: string;
    readonly detail: string | null;
    readonly city: string;
    readonly zipcode: string | null;
    readonly country?: CountryDto;
};

export type CompanyDeliveryAddressDto = CompanyAddressDto & {
    readonly id: number;
    readonly name: string;
    is_active: boolean;
    readonly is_default_for_vehicles: boolean;
    readonly is_default_for_documents: boolean;
    readonly remarks: string | null;
    readonly phone_number: string | null;
    readonly has_transport?: boolean;
};

export type PostCompanyDeliveryAddressRequestBody = {
    readonly name: string;
    readonly is_default_for_documents: boolean;
    readonly is_default_for_vehicles: boolean;
    readonly remarks: string | null;
    readonly phone_number: string | null;
    readonly street: string;
    readonly number: string;
    readonly detail: string | null;
    readonly city: string;
    readonly zipcode: string | null;
    readonly id_country: number;
};

export type PatchCompanyDeliveryAddressRequestBody = {
    readonly name: string;
    readonly detail: string | null;
    readonly is_active: boolean;
    readonly is_default_for_vehicles: boolean;
    readonly is_default_for_documents: boolean;
    readonly remarks: string | null;
    readonly phone_number: string | null;
};

export type AccountManagerDto = {
    readonly id: number;
    readonly firstname: string;
    readonly lastname: string;
    readonly phone: string;
    readonly phone_mobile: string;
    readonly supported_languages: LanguageDto[];
    readonly email: string;
};

export type CompanyDto = {
    readonly id: bigint | number;
    readonly name: string;
    readonly commercial_name: string | null;
    readonly registration_number: string | null;
    readonly website_url: string | null;
    readonly email: string;
    readonly phone_number: string;
    readonly address: CompanyAddressDto;
    readonly account_manager: AccountManagerDto;
    readonly status: CompanyStatusDto;
};

export type ContactProspectMetadataDto = {
    company_name: string;
    vat_number: string;
};

export type CompanyFinancialDto = {
    readonly invoicing_email: string;
    readonly payment_term_days: number;
    readonly credit_limit_euro: number;
    readonly credit_remaining_euro: number;
    readonly outstanding_payment_amount_euro: number; // TODO remove because of missing in swagger
    readonly outstanding_balance_euro: number;
    readonly default_order_flow: {
        id: number;
        name: string;
        on_credit: boolean;
    };
};

export type DefaultOrderFlow = {
    readonly id: number;
    readonly name: string;
    readonly on_credit: boolean;
};

export type CompanyFinancialRecordDto = {
    readonly invoicing_email: string;
    readonly credit_limit_eur: number;
    readonly credit_remaining_eur: number;
    readonly default_order_flow: DefaultOrderFlow;
};
export type CompanyFinancialForm = {
    readonly invoicing_email: string;
    readonly credit_limit_eur: string;
    readonly credit_remaining_eur: string;
    readonly name: string;
    readonly on_credit: boolean;
};

export type CompanyLegalDocTypeDto = {
    readonly id: number;
    readonly name: string;
    readonly is_mandatory: boolean;
};

export type PatchCompanyUserRequestBody = {
    readonly id_role?: number;
    readonly is_enabled?: boolean;
};

export type PutCompanyRequestBody = {
    readonly id: bigint | number | null;
    readonly name: string;
    readonly full_name: string;
    readonly status: string;
    readonly commercial_name: string | null;
    readonly registration_number: string | null;
    readonly website_url: string | null;
    readonly email: string;
    readonly phone_number: string;
    readonly address_street: string;
    readonly address_number: string;
    readonly address_detail: string | null;
    readonly address_city: string;
    readonly address_zipcode: string | null;
    readonly address_id_country: number;
};

export type PutCompanyProfileRequestBody = {
    readonly id_customer_type: number;
    readonly average_vehicles_in_stock: number;
    readonly average_vehicles_sold_annually: number;
    readonly ids_brands: number[];
    readonly ids_bodytypes: number[];
};

export type CompanyProfileDto = {
    readonly average_vehicles_in_stock: number;
    readonly average_vehicles_sold_annually: number;
    type: CompanyType;
    brands: BrandType[];
    bodytypes: BodyType[];
};

export type PostCompanyUsersRequestBody = {
    // TODO mismatch with swagger
    readonly firstname: string;
    readonly lastname: string;
    readonly email: string;
    readonly id_language: number;
    readonly id_role: number;
    readonly id_company: number | null;
    readonly phone: string;
    readonly mobile: string;
    readonly fax: string;
    readonly contactPerson: string;
};
export type TeamMemberRequestBody = {
    readonly id_company: number | null;
    readonly firstname: string | null;
    readonly lastname: string | null;
    readonly email: string | null;
    readonly id_language: number | null;
    readonly id_role: number | null;
    readonly marketing_subscription: boolean;
};

// Filter Statistics ---------------------------------------------------------------------------------------------------
/* Base class - All inherit from this one - e.g. they all have a name for sure. */
export type FilterItemDto = {
    readonly name: string;
    readonly counter: number;
};

export type EnumeratorFilterItemDto = FilterItemDto & {
    readonly id: number;
};

export type ColorFilterItemDto = EnumeratorFilterItemDto & {
    readonly hex: string;
    readonly cmyk: string;
};

export type StringFilterItemDto = FilterItemDto & {
    readonly id: string;
};

export type RangeFilterItemDto = FilterItemDto & {
    readonly min_value: number;
    readonly max_value: number;
    readonly unit: string;
};

export enum VehicleFilterType {
    /* Enum types */
    condition = "condition",
    fueltype = "fueltype",
    brand = "brand",
    gearbox = "gearbox",
    bodywork = "bodywork",
    bodytype = "bodytype",
    availability = "availability",
    provider = "provider",
    advanced = "advanced",
    /* Range types */
    mileage = "mileage",
    firstregistrationYear = "firstregistration_year",
    emissionNedc = "emission_nedc",
    emissionWltp = "emission_wltp",
    damage = "damage",
    /* ColorFilterItemDto types */
    color = "color",
    /* StringFilterItemDto types */
    model = "model",
    price = "price",
}

export type PublicVehicleFiltersDto = {
    readonly [VehicleFilterType.brand]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.model]: StringFilterItemDto[];
    readonly [VehicleFilterType.fueltype]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.condition]: EnumeratorFilterItemDto[];
};

export type AuthenticatedVehicleFiltersDto = PublicVehicleFiltersDto & {
    readonly [VehicleFilterType.color]: ColorFilterItemDto[];
    readonly [VehicleFilterType.gearbox]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.firstregistrationYear]: RangeFilterItemDto[];
    readonly [VehicleFilterType.damage]: RangeFilterItemDto[];
    readonly [VehicleFilterType.emissionNedc]: RangeFilterItemDto[];
    readonly [VehicleFilterType.emissionWltp]: RangeFilterItemDto[];
    readonly [VehicleFilterType.mileage]: RangeFilterItemDto[];
    readonly [VehicleFilterType.price]: RangeFilterItemDto[];
    readonly [VehicleFilterType.availability]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.provider]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.bodywork]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.bodytype]: EnumeratorFilterItemDto[];
    readonly [VehicleFilterType.advanced]: EnumeratorFilterItemDto[];
};

export type AdvancedFilterItemDto = {
    readonly lowered_in_price: number;
    readonly new_in_stock: number;
    readonly matches_company_profile: number;
};

export type FilterStatisticsDto = {
    readonly match_counter: number;
    readonly filters: AuthenticatedVehicleFiltersDto & AdvancedFilterItemDto;
};
// Vehicles ------------------------------------------------------------------------------------------------------------

export type ExternalReferenceDto = {
    // TODO: delete!
    id_fm: number;
    id_sc: number;
    formatted_string: string;
};

export type BrandDto = {
    id: number;
    name: string;
};

export type FactoryEquipmentDto = {
    equipments: string[];
};

export type InteriorColorDto = {
    id: number;
    name: string;
    hex: string;
    cmyk: string;
};

export type ExteriorColorDto = InteriorColorDto & {
    is_light: boolean;
};

export type ExteriorColorVariantDto = {
    color: ExteriorColorDto;
    vehicle_counter: number;
    vehicle_ids: readonly number[];
};

export type VehicleDocumenttypeDto = {
    id: number;
    name: string;
};

export type VehicleDocumentAvailabilityDto = {
    documenttype: VehicleDocumenttypeDto;
    available: boolean;
    from_date: number | null;
};

export type AccessoryDto = {
    id: number;
    name: string;
};

export type VehicleAccessoryAvailabilityDto = {
    accessory: AccessoryDto;
    available: boolean;
};

export type ConditionDto = {
    readonly id: number;
    readonly name: string;
};

export type BodyworkDto = {
    readonly id: number;
    readonly name: string;
};

export type BodytypeDto = {
    readonly id: number;
    readonly name: string;
};

export type FueltypeDto = {
    readonly id: number;
    readonly name: string;
};

export type GearboxDto = {
    readonly id: number;
    readonly name: string;
};

export type OptionDto = {
    readonly id: number;
    readonly name: string;
    readonly icon_href: string;
};

export interface VehicleWarrantyDto {
    start_date: number;
    length_months: number;
    mileage_limit?: number | null;
}

export interface BatteryWarrantyDto {
    start_date: number;
    length_months: number;
    mileage_limit?: number | null;
    start_mileage?: number | null;
}

export interface VehicleMarketPriceDto {
    country: CountryDto;
    price: number;
}

export interface VehicleCatalogPriceDto {
    country: CountryDto;
    price: number;
    discount_percentage: number;
}

export type VehiclePricingDto = {
    readonly list_price_euro: number;
    readonly is_margin: boolean;
};

export type VehicleAvailabilityDto = {
    readonly id: number;
    readonly name: string;
};

export type VehicleFavoriteStatusDto = {
    readonly is_favorite: boolean;
    readonly timestamp: number;
};

export type VehicleThumbnailImageDto = {
    readonly href_small: string;
    readonly href_medium: string;
    readonly href_large: string;
};

export type VehicleImageDto = VehicleThumbnailImageDto & {
    readonly id: bigint;
};

export type PromotionDto = {
    readonly id: number;
    readonly name: string;
};

export type DamageLocationDto = {
    readonly id: number;
    readonly name: string;
};

export type DamageTypeDto = {
    readonly id: number;
    readonly name: string;
};

export type Promotion = {
    readonly id: number;
    readonly name: string;
};
export type VehicleDamageRecordDto = {
    readonly location: DamageLocationDto;
    readonly types: DamageTypeDto[];
};

export type VehicleDamageDto = {
    readonly description: string;
};

export type UpholsteryDto = {
    readonly id: number;
    readonly name: string;
};

export type PowertrainTypeDto = {
    readonly id: number;
    readonly name: string;
};

export type EuroEmissionStandardDto = {
    readonly id: number;
    readonly name: string;
};

export type DrivetrainDto = {
    readonly id: number;
    readonly name: string;
};

export type OnboardChargerDto = {
    readonly id: number;
    readonly name: string;
};

export type PowertrainElectricDto = {
    readonly autonomy_km: number | null;
    readonly autonomy_city_km: number | null;
    readonly battery_capacity_kw: number | null;
    readonly charging_capacity_kw: number | null;
    readonly fast_charging_capacity_kw: number | null;
    readonly onboard_charger_type: OnboardChargerDto | null;
    readonly fast_charging_capable: boolean | null;
    readonly consumption_wh_km: number | null;
};

export type PowertrainFossileDto = {
    readonly amount_of_gears: number | null;
    readonly cylinder_amount: number | null;
    readonly cylinder_capacity_cc: number | null;
    readonly nedc_consumption_urban_l100km: number | null;
    readonly nedc_consumption_extraurban_l100km: number | null;
    readonly nedc_consumption_combined_l100km: number | null;
    readonly wltp_consumption_low_l100km: number | null;
    readonly wltp_consumption_medium_l100km: number | null;
    readonly wltp_consumption_high_l100km: number | null;
    readonly wltp_consumption_extrahigh_l100km: number | null;
    readonly wltp_consumption_combined_l100km: number | null;
    readonly emission_co2_nedc_combined: number | null;
    readonly emission_co2_wltp_combined: number | null;
};

export type PowertrainHybridDto = {
    readonly amount_of_gears: number | null;
    readonly cylinder_amount: number | null;
    readonly cylinder_capacity_cc: number | null;
    readonly wltp_consumption_low_l100km: number | null;
    readonly wltp_consumption_medium_l100km: number | null;
    readonly wltp_consumption_high_l100km: number | null;
    readonly wltp_consumption_extrahigh_l100km: number | null;
    readonly wltp_consumption_combined_l100km: number | null;
    readonly wltp_consumption_weighted_combined_l100km: number | null;
    readonly wltp_emission_co2_combined: number | null;
    readonly electric_autonomy_km: number | null;
    readonly electric_autonomy_city_km: number | null;
    readonly battery_capacity_kw: number | null;
    readonly charging_capacity_kw: number | null;
    readonly fastcharging_capacity_kw: number | null;
    readonly onboard_charger_type: OnboardChargerDto | null;
    readonly fast_charging_capable: boolean | null;
    readonly engine_power_electric_kw: number | null;
    readonly engine_power_fossile_kw: number | null;
};

export type PowertrainDto = {
    readonly type: PowertrainTypeDto;
    readonly gearbox: GearboxDto;
    readonly emission_standard: EuroEmissionStandardDto;
    readonly fueltype: FueltypeDto;
    readonly drivetrain: DrivetrainDto | null;
    readonly motorisation: string | null;
    readonly power_hp: number;
    readonly power_kw: number;
    readonly specification: PowertrainElectricDto &
        PowertrainFossileDto &
        PowertrainHybridDto;
};

export type GetPublicVehiclesQueryParams = {
    /* Public Filter conditions */
    condition: readonly number[];
    brand: readonly number[];
    model: readonly string[];
    fueltype: readonly number[];
};

export type GetAuthenticatedVehiclesQueryParams = GetPublicVehiclesQueryParams & {
    /* Authenticated Filter Conditions */
    color: readonly number[];
    gearbox: readonly number[];
    promotion: readonly number[];
    option: readonly number[];
    bodywork: readonly number[];
    bodytype: readonly number[];
    availability: readonly number[];
    provider: readonly number[];
    emission_nedc_min: null | number;
    emission_nedc_max: null | number;
    emission_wltp_min: null | number;
    emission_wltp_max: null | number;
    mileage_min: null | number;
    mileage_max: null | number;
    damage_amount_min: null | number;
    damage_amount_max: null | number;
    firstregistration_year_min: null | number;
    firstregistration_year_max: null | number;
    price_min: null | number;
    price_max: null | number;
    has_promotion: null | boolean;
    new_in_stock?: null | boolean;
    lowered_in_price?: null | boolean;
    matches_company_profile?: null | boolean;
};

export type VehicleOriginCountryTypeDto = {
    id: number;
    name: string;
};

export type RuleBreakdownDto = {
    brand: string;
    model: string | null;
    quantity: number;
}

export type VehicleAvailableDealDto = {
    readonly deal_id: number;
    readonly deal_name: string;
    readonly brands: string[];
    readonly models: string[];
    readonly required_quantity: number;
    readonly current_quantity: number;
    readonly missing_quantity: number;
    readonly reduction_amount: number;
    readonly start_date: string;
    readonly end_date: string;
    readonly already_applied: boolean;
    readonly already_applied_times: number;
    readonly rule_breakdown: RuleBreakdownDto[];
};

export type VehicleDto = {
    readonly id: number;
    readonly brand: BrandDto;
    readonly model: string;
    readonly description: string | null;
    readonly manufacturing_date: bigint | null;
    readonly firstregistration_date: number | null;
    readonly finish: string | null;
    readonly number_doors: number | null;
    readonly mileage: number;
    readonly number_seats: number | null;
    readonly color: ExteriorColorDto;
    readonly color_factory: string | null;
    readonly color_interior_factory: string | null;
    readonly color_interior: InteriorColorDto | null;
    readonly weight: number | null;
    readonly condition: ConditionDto;
    readonly bodywork: BodyworkDto;
    readonly bodytype: BodytypeDto;
    readonly upholstery: UpholsteryDto | null;
    readonly powertrain: PowertrainDto;
    readonly price_current: VehiclePricingDto;
    readonly price_previous: VehiclePricingDto;
    readonly origin_country_type: VehicleOriginCountryTypeDto;
    readonly deals: string[];
    readonly vehicle_message?: string | null;
    readonly available_deals_for_vehicle: VehicleAvailableDealDto[];
};

export type PublicVehicleDto = VehicleDto & {
    readonly price_current: VehiclePricingDto | null;
    readonly price_previous: VehiclePricingDto | null;
    readonly provider: EnumeratorFilterItemDto | null;
    readonly availability: VehicleAvailabilityDto;
};

export type AuthenticatedVehicleDto = PublicVehicleDto & {
    readonly price_current: VehiclePricingDto;
    readonly price_previous: VehiclePricingDto | null;
};

export type PublicVehicleTileDto = {
    readonly id: number;
    readonly brand: BrandDto;
    readonly model: string;
    readonly motorisation: string | null;
    readonly finish: string | null;
    readonly color: ExteriorColorDto;
    readonly mileage: number;
    readonly price_current: VehiclePricingDto | null;
    readonly price_previous: VehiclePricingDto | null;
    readonly thumbnail_image: VehicleThumbnailImageDto | null;
    readonly has_promotion: boolean;
    readonly has_deal: boolean;
};

export type PublicPromotionalVehicleTileDto = {
    readonly id: number;
    readonly brand: BrandDto;
    readonly model: string;
    readonly motorisation: string | null;
    readonly finish: string | null;
    readonly color: ExteriorColorDto;
    readonly mileage: number;
    readonly price_current: VehiclePricingDto;
    readonly price_previous: VehiclePricingDto | null;
    readonly thumbnail_image: VehicleThumbnailImageDto | null;
    readonly has_promotion: boolean;
};

export type AuthenticatedVehicleTileDto = PublicVehicleTileDto & {
    readonly is_favorite: boolean;
    readonly price_current: VehiclePricingDto;
    readonly price_previous: VehiclePricingDto | null;
};

export type PaginationMetadata = {
    readonly current_page: number;
    readonly items_per_page: number;
    readonly total_pages: number;
};

// Vehicle Groupings ---------------------------------------------------------------------------------------------------
export type VehicleGroupingQueryParameters = {
    grouping_mode: number;
    hashcode: number | null;
};

export type VehicleGroupingModeDto = {
    readonly id: number;
    readonly description: string;
};

export type VehicleGroupingDto = {
    readonly mode: VehicleGroupingModeDto;
    readonly hash: number;
};

export type PublicVehicleGroupDto = {
    readonly grouping_mode: VehicleGroupingModeDto;
    readonly hashcode: number;
    readonly vehicle_counter: number;
    readonly vehicle_ids: number[];
    readonly mileage_min: number;
    readonly mileage_max: number;
    readonly exterior_color_variants: ExteriorColorVariantDto[];
    readonly brands: BrandDto[];
    readonly models: string[];
    readonly motorisations: string[];
    readonly finishes: string[];
    readonly thumbnail_images: VehicleImageDto[];
    readonly firstregistration_date_max: number;
    readonly firstregistration_date_min: number;
    readonly has_deal: boolean;
};

export type AuthenticatedVehicleGroupDto = PublicVehicleGroupDto & {
    readonly price_min: number;
    readonly price_max: number;
};

export type AuthenticatedVehicleGroupsResponseDataDto = {
    readonly match_counter: number;
    readonly groups: AuthenticatedVehicleGroupDto[];
};

// Shopping cart -------------------------------------------------------------------------------------------------------
export type ShoppingCartItemDto = {
    readonly vehicle: AuthenticatedVehicleDto;
    readonly promotion: Promotion;
    readonly is_available: boolean;
};

export type ShoppingCartDealDto = {
    readonly deal_id: number;
    readonly deal_name: string;
    readonly brand: string;
    readonly model: string | null;
    readonly required_quantity: number | null;
    readonly current_quantity: number | null;
    readonly missing_quantity: number | null;
    readonly reduction_amount: number;
    readonly start_date: string;
    readonly end_date: string;
};

export type ShoppingCartDto = {
    readonly available_deals: VehicleAvailableDealDto[];
    readonly items: ShoppingCartItemDto[];
    readonly total_price_without_discount_euro: number;
    readonly total_discount_euro: number;
    readonly total_discount_percentage: number;
    readonly total_price_euro: number;
};

export type TransportExpeditorDto = {
    readonly id: number;
    readonly name: string;
};

export enum VehicleFuelType {
    "Hydrogen" = 1,
    "Electric" = 2,
    "Plug-in Hybrid Diesel" = 3,
    "Plug-in Hybrid Petrol" = 4,
    "Hybrid Diesel" = 5,
    "Hybrid Petrol" = 6,
    "CNG" = 7,
    "Petrol" = 8,
    "Diesel" = 10,
    "LPG" = 11,
}

// Orders --------------------------------------------------------------------------------------------------------------
export type OrderStatusDto = {
    readonly id: number;
    readonly name: string;
};

export type OrderStatusStatisticDto = {
    readonly status: OrderStatusDto;
    readonly orders_counter: number;
    readonly vehicles_counter: number;
    readonly orders_action_required_counter: number;
    readonly vehicles_action_required_counter: number;
};

export type OrderStatisticsDto = {
    readonly stats_per_status: OrderStatusStatisticDto[];
    readonly total_orders_counter: number;
    readonly total_vehicles_counter: number;
};

export type OrderTypeDto = {
    readonly id: number;
    readonly name: string;
};

export type OrderFlowDto = {
    readonly id: number;
    readonly name: string;
};

export type OrderApprovalDto = {
    readonly approval_contact: ContactDto;
    readonly timestamp: bigint;
};

export type OrderDto = {
    readonly id: bigint;
    readonly backoffice_reference: string | undefined;
    readonly customer_reference: string | undefined;
    readonly status: OrderStatusDto;
    readonly type: OrderTypeDto;
    readonly date: bigint;
    readonly vehicle_counter: number;
    readonly total_amount_euro_excl_vat: number;
    readonly remarks: string | null;
    readonly delivery_address: CompanyDeliveryAddressDto;
    readonly account_manager: AccountManagerDto;
    readonly purchase_contact: ContactDto;
    readonly approval: OrderApprovalDto | undefined;
    readonly transport_expeditor: TransportExpeditorDto;
    readonly flow: OrderFlowDto;
    readonly description: string;
};

export type VatRegimeDto = {
    readonly id: number;
    readonly name: string;
    readonly vat_percentage: number;
};

export type OrderVehicleStatusDto = {
    readonly id: number;
    readonly name: string;
};

export type OrderVehicleDto = {
    readonly id: bigint;
    readonly vehicle: VehicleDto;
    readonly price_transport_ex_vat: number | null;
    readonly price_admin_handling_ex_vat: number | null;
    readonly price_vehicle_ex_vat: number | null;
    readonly vat_regime: VatRegimeDto;
    readonly vat_amount: number | null;
    readonly total_price_including_vat: number | null;
    readonly transport_unit_weight: number;
    readonly status: OrderVehicleStatusDto;
    readonly availability: VehicleAvailabilityDto | null;
    readonly vehicle_vin: string | null;
    readonly thumbnail_image?: VehicleThumbnailImageDto;
    readonly deal_reduction: number | null;
};

export type OrderPriceDetailDto = {
    readonly total_price_vehicles_ex_vat: number;
    readonly total_price_transport_ex_vat: number | null;
    readonly total_price_admin_handling_ex_vat: number | null;
    readonly total_transport_units: number | null;
    readonly total_vat_amount: number | null;
    readonly total_price_including_vat: number | null;
    readonly total_deal_reduction: number | null;
    readonly deal_reduction: number | null;
    readonly priceAfterDeal: number | null;
};

export type VehicleAccessoryShipmentMetadataDto = {
    readonly accessory: AccessoryDto;
    readonly is_shipped: boolean;
    readonly date: bigint | null;
};

export type VehicleDocumentShipmentMetadataDto = {
    readonly document: VehicleDocumenttypeDto;
    readonly is_shipped: boolean;
    readonly date: bigint | null;
};

export type VehicleShipmentMetadataDto = {
    readonly date_expected: bigint | null;
    readonly date_actual: bigint | null;
    readonly cmr_customer_present: boolean;
    readonly cmr_customer_approved: boolean;
};

export type PostOrderDeclineRequestBody = {
    readonly reason: string;
};

export type PaymentTypeDto = {
    readonly id: number;
    readonly name: string;
};

export type PaymentStatusDto = {
    readonly id: number;
    readonly name: string;
};

export type PaymentDto = {
    readonly id: bigint;
    readonly type: PaymentTypeDto;
    readonly status: PaymentStatusDto;
    readonly total_amount_euro: number;
    readonly reference: string | null;
    readonly date: number;
};

export type OrderPaymentDetailsDto = {
    readonly order_total_amount_including_vat: number | null;
    readonly amount_paid_confirmed: number;
    readonly amount_paid_pending: number;
    readonly payments: PaymentDto[];
};

export enum PaymentType {
    "Manual Transfer" = 1,
    "Mollie" = 2,
}

export type PostOrderPaymentsRequestBody = {
    readonly ids_order_vehicles: string[];
    readonly id_payment_type: PaymentType;
};

export type OrderVehiclePaymentLinkDto = {
    readonly vehicle: VehicleDto;
    readonly thumbnail_image: VehicleThumbnailImageDto;
    readonly payment_amount: number;
};

export type PaymentMollieMetadataDto = {
    readonly mollie_id: string;
    readonly checkout_url_href: string;
    readonly checkout_url_type: string;
};

export type PostOrderPickupDateRequestBody = {
    readonly ids_order_vehicles: string[];
    readonly date: string;
};

// TODO: remove? -------------------------------------------------------------------------------------------------------
export enum VehicleFeature {
    AIRCO = "2",
    CRUISE_CONTROL = "32",
    HEATED_SEAT = "19",
    RADIO = "15",
    LIGHT_SENSOR = "60",
    GPS = "3",
    GPS_PLUS = "4",
    RADIO_CD = "13",
}

export const VehicleOptionIconIds = {
    166: "airco",
    32: "cruiseco",
    19: "seat",
    15: "radio",
    13: "radio",
    60: "lightsensor",
    3: "gps",
    4: "gps",
};

export enum VehicleLabel {
    newIn = "new In",
    specialDeal = "special Deal",
    reducedPrice = "reduced Price",
}

export type ImagineOptions = ReadonlyRecord<string, string>;

export type Stock = {
    readonly vin: string;
    readonly color_id: string;
    readonly color_name: string;
    readonly hex: string;
    readonly cmyk: string;
    readonly stock: number;
    readonly images: readonly (string | null | undefined)[];
    readonly vivition_player_html: null | string;
    readonly vivition_player_thumb_html: null | string;
    readonly imagine_options: ImagineOptions;
    readonly ids: readonly { readonly id: number; readonly vin: string }[];
    readonly paint_id: string | null;
};

export type GetVehiclesBody = {
    readonly data: readonly VehicleDto[];
    readonly meta:
        | null
        | (Pagination & {
              readonly filters: null | AuthenticatedVehicleFiltersDto;
              readonly mainFilter1: null | VehicleFilterType;
              readonly mainFilter2: null | VehicleFilterType;
              readonly mainFilter3: null | VehicleFilterType;
              readonly mainFilter4: null | VehicleFilterType;
              readonly mainFilter5: null | VehicleFilterType;
              readonly mainFilter6: null | VehicleFilterType;
              readonly total_cars: number;
          });
};

export type VehicleGroupDto = AuthenticatedVehicleGroupDto | PublicVehicleGroupDto;

export type GetVehicleGroupsBody = {
    readonly data: readonly PublicVehicleDto[];
    readonly meta:
        | null
        | (Pagination & {
              readonly carGroup: VehicleGroupDto;
              readonly filters: null | AuthenticatedVehicleFiltersDto;
              readonly mainFilter1: null | VehicleFilterType;
              readonly mainFilter2: null | VehicleFilterType;
              readonly mainFilter3: null | VehicleFilterType;
              readonly mainFilter4: null | VehicleFilterType;
          });
};

export type ContactInfoForm = {
    readonly email: string;
    readonly firstname: string;
    readonly lastname: string;
    readonly mobile: string;
    readonly phone: string;
    readonly representative: string;
    readonly language: AppLanguageCode;
};

export type CompanyInfoForm = {
    readonly companyName: string;
    readonly vatNumber: string;
    readonly city: string;
    readonly representative: string;
    readonly website: string;
    readonly postalcode: string;
    readonly countryIso2Code: AppCountryCode;
    readonly address: string;
    readonly status: EnumCompanyStatus;
    readonly accountManager: string;
    readonly paymentTerm: boolean;
    readonly creditLimit?: number;
    readonly remainingCredit?: number;
};

export type GetCompanyInfoForm = CompanyInfoForm & {
    readonly clientId: number;
    readonly accountManager: string;
    readonly documents: readonly {
        readonly docType: number;
        readonly docName: string;
    }[];
};

export type LoginInfoForm = {
    readonly username: string;
    readonly oldPassword: string;
    readonly newPassword: string;
    readonly repeatedPassword: string;
};

export type MyContactInfo = {
    readonly firstname: string;
    readonly lastname: string;
    readonly email: string;
    readonly phone: string;
    readonly mobile: string;
    readonly representative: string;
    readonly language: AppLanguageCode.en;
};

export type MyCompanyInfo = {
    readonly companyName: string;
    readonly vatNumber: string;
    readonly address: string;
    readonly number: string;
    readonly postalcode: string;
    readonly city: string;
    readonly countryIso2Code: AppCountryCode;
    readonly website: string;
    readonly representative: string;
};

export type MyLoginInfo = {
    readonly username: string;
};

export type ContactUsForm = {
    firstname: string;
    lastname: string;
    email: string;
    subject: string;
    message: string;
};

export type ContactUsFormSubjects = {
    label: string;
    value: string;
};

export type ForgotPasswordForm = {
    readonly request: {
        readonly url: "/forgot-password";
        readonly method: "post";
        readonly body: {
            email: string;
        };
    };
};

// My orders
export enum OrderStatus {
    to_approve = "1",
    on_order = "2",
    to_pay = "3",
    to_transport = "4",
    processing_documents = "5",
    completed = "6",
}

export enum OrderVehicleStatus {
    // TO APPROVE
    "Finalizing" = 1,
    "Awaiting Approval" = 2,
    // ON ORDER
    "Processing" = 3,
    // TO PAY - DEFERRAL USER
    "Awaiting Prepayment" = 4,
    "Processing Prepayment" = 5,
    // TO PAY - NORMAL USER
    "Awaiting Payment" = 6,
    "Processing Payment" = 7,
    // TO TRANSPORT - OWN PICKUP
    "Awaiting Pickup Date" = 8,
    "Ready For Pickup" = 9,
    // TO TRANSPORT - CARCONNEX DELIVERY
    "Planning Ongoing" = 10,
    "Transport Planned" = 11,
    // PROCESSING DOCUMENTS
    "Awaiting customer CMR" = 12,
    "CMR Overdue" = 13,
    "Processing CMR" = 14,
    "Awaiting Shipment" = 15,
    // COMPLETED
    "Completed" = 16,
    "Cancelled" = 17,
}

export enum OrderSubStatus {
    ready_for_pickup = "ready_for_pickup",
    planning_ongoing = "planning_ongoing",
    transport_planned = "transport_planned",
    waiting_cmr = "waiting_cmr",
    processing_cmr = "processing_cmr",
    cmr_overdue = "cmr_overdue",
}

export type OrderPriceDetail = {
    readonly price: number | string;
    readonly label: string;
    readonly currency: string;
};

export type OrderDtoOld = {
    readonly id: string;
    readonly status: OrderStatus;
    readonly created_at: number;
    readonly expires_at: number;
    readonly first_reg_date: number;
    readonly total_price: number;
    readonly total_price_vat_inc: number;
    readonly vat_amount: number;
    readonly currency: string;
    readonly ready_to_approve: boolean;
    readonly price_details: readonly OrderPriceDetail[];
    readonly est_doc_available_date: number;
    readonly available: string;
    readonly car_id: number;
    readonly title: string;
    readonly description: readonly string[];
    readonly images: readonly (string | null | undefined)[];
    readonly payment_reference: string;
    readonly pfnr: string;
    readonly delivery_address: string;
    readonly manufactured_at: number;
    readonly brand: string;
    readonly model: string;
    readonly finish: string;
    readonly variant: string;
    readonly body_size: string;
    readonly model_range: string;
    readonly stocks: readonly Stock[];
    readonly color: string;
    readonly fuel: string;
    readonly vin: string;
    readonly webCreated: boolean;
    readonly documentAvailable: {
        readonly coc: string;
        readonly reg: string;
    };
    readonly mollyStatus: string;
    readonly mollyId: string;
    readonly advancedPayment?: number;
    readonly manualTransfer?: number;
    readonly pickupDate?: number;
    readonly transportDate?: number;
    readonly paymentReceived?: number;
    readonly subStatus: OrderSubStatus;
    readonly cmr?: number;
    readonly group_reference?: number;
};

export type OrdersVehicleType = {
    readonly id: number;
    readonly vin: string;
    readonly status: OrderStatus;
    readonly molly_status: string;
    readonly title: string;
    readonly description: string;
    readonly color: string;
    readonly manufactured_at: number;
    readonly price_details: readonly {
        readonly price: number;
        readonly label: string;
        readonly currency: string;
    }[];
    readonly total: number; // total price (float), vat excluded
    readonly currency: string;
    readonly images: readonly (string | null | undefined)[];
    readonly payment_reference: string;
    readonly estimated_date: number;
    readonly details: {
        readonly block1: readonly string[] /* https://i.imgur.com/pwq8v3V.png */;
        readonly block2: readonly string[] /* https://i.imgur.com/JKnxTnX.png */;
    };
    readonly order_id: number;
    readonly order_created_at: number;
    readonly brand: string;
    readonly model: string;
    readonly finish: string;
    readonly variant: string;
    readonly body_size: string;
    readonly model_range: string;
    readonly stocks: readonly Stock[];
};

export interface IOrderInfoTab {
    readonly action_needed: number;
    readonly items: number;
}

export type OrdersTabsType = {
    readonly all: number;
    readonly completed: OrderStatusStatisticDto;
    readonly on_order: OrderStatusStatisticDto;
    readonly to_approve: OrderStatusStatisticDto;
    readonly to_pay: OrderStatusStatisticDto;
    readonly to_transport: OrderStatusStatisticDto;
    readonly processing_documents: OrderStatusStatisticDto;
};

export enum OrdersTableColumns {
    orderStatus = "order.orderStatus",
    orderSubStatus = "order.orderSubStatus",
    orderDate = "createdOn",
    orderDetails = "vehicle.title",
    orderAmount = "price",
    vehicles = "vehicles",
}

export enum OrdersFields {
    Price = 1,
    CarsAmount = 2,
    Date = 3,
    Reference = 4,
}

export enum OrdersSortDirection {
    ASC = 1,
    DESC = 2,
}
export type OrderDetail = {
    readonly id: string;
    readonly status: OrderStatus;
    readonly pending_payment: boolean; // true only if all the cars with the status
    readonly created_at: number;
    readonly expires_at: number; // this field has different meanings for different statuses, for example an expiration data to appprove an order or an estimated date to process an order etc (see design)
    readonly reference: string; // "PF/115740"
    readonly count: number; // number of cars,
    readonly total_price: number;
    readonly current_status_price: number | null;
    readonly currency: string;
    readonly ready_to_approve: boolean;
    readonly car_statuses: ReadonlyRecord<
        | OrderStatus.to_approve
        | OrderStatus.on_order
        | OrderStatus.to_pay
        | OrderStatus.to_transport
        | OrderStatus.processing_documents
        | OrderStatus.completed,
        {
            readonly count: number;
            readonly total: number;
            readonly description: string;
            readonly total_price: number; // VAT excluded (float)
            readonly paid_price: number; // total price paid so far
            readonly currency: string; // EUR, RUB ..
        }
    >;
    readonly cars: readonly OrdersVehicleType[];
};

export type OrderDocument = {
    readonly name: string;
    readonly file_type: string;
    readonly reference:
        | "final_invoice"
        | "payment_proof"
        | "registration_proof"
        | "coc"
        | "cmr"
        | "pua"
        | string;
    readonly url: string;
};

export type OrderCarDocuments = {
    readonly order_reference: string;
    readonly order_date: number;
    readonly car: OrdersVehicleType;
    readonly documents: readonly OrderDocument[];
};

export type CarHistoryCar = OrdersVehicleType & {
    readonly order_id: number;
    readonly order_created_at: number; // milliseconds
    readonly order_reference: string;
};

export enum CarHistoryTablesColumns {
    orderDate = "createdOn",
    item = "item",
}

export type Pager = {
    readonly per_page: number;
    readonly page: number;
};

export type Fee = {
    readonly cars: number;
    readonly price: number;
};

export type AddressGet = {
    readonly id: string;
    readonly address: string;
    readonly companyId?: number;
    readonly city?: string;
    readonly country: string;
    readonly fees: readonly Fee[];
    readonly default_address: boolean;
    readonly name?: string;
    readonly house_number: string;
};

export type AddressPost = {
    readonly name?: string;
    readonly address: string;
    readonly postalCode: number;
    readonly city: string;
    readonly default_address: boolean;
    readonly phone?: string;
    readonly fax?: string;
    readonly country: string;
    readonly house_number: string;
};

export enum TransportType {
    pickup = "pickup",
    carconnex = "carconnex_transport",
}

export type DeclineForm = {
    readonly reason: string;
};

export interface ShoppingCartCheckoutSimulationItemDto {
    /** Extends the PublicVehicle object with properties only authenticated users can see. */
    vehicle?: AuthenticatedVehicleDto;
    is_available?: boolean;
    /** Model that represents a VAT regime. */
    thumbnail_image?: VehicleThumbnailImageDto;
    promotion: Promotion;
    vat_regime?: VatRegimeDto;
    transport_price?: number | null;
    total_price_including_vat?: number | null;
    total_vat?: number | null;
    admin_handling_price?: number | null;
    vat_amount_vehicle?: number | null;
    vat_amount_transport?: number | null;
    vat_amount_admin_handling?: number | null;
    transport_price_including_vat?: number | null;
    admin_handling_price_including_vat?: number | null;
    total_vehicle_price?: number | null;
    vehicle_price_including_vat?: number | null;
}

export interface ShoppingCartCheckoutSimulationDto {
    items?: ShoppingCartCheckoutSimulationItemDto[];
    vehicles_total_price_without_discount?: number;
    vehicles_total_discount?: number;
    vehicles_total_discount_percentage?: number;
    vehicles_total_price?: number;
    total_vat?: number;
    total_vehicle_price?: number;
    vehicles_total_price_including_vat?: number;
    total_transport_cost?: number | null;
    total_transport_price?: number | null;
    total_price?: number | null;
    total_price_including_vat?: number | null;
    total_admin_handling_price: number | null;
    total_admin_handling_price_including_vat: number | null;
    total_deal_reduction: number | null;
}

export interface PostUserShoppingCartCheckoutResponse {
    order_id: number;
    order_status: OrderStatusDto;
}
